/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Cloud, Plus, Loader, Server } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useToast } from '../hooks/use-toast';
import { 
  DeploymentCard, 
  DeploymentFilters, 
  DeploymentListHeader, 
  DeploymentWizard,
  DeploymentDetails 
} from '../components/deployments';
import {
  listDeployments,
  getDeployment,
  getResourceTemplates,
  createDeployment,
  startDeployment,
  stopDeployment,
  restartDeployment,
  deployFromGit,
  deleteDeployment,
  getDeploymentEvents,
  runDatabaseMigrations,
  createDatabaseBackup,
} from '../lib/api';
import type { 
  Deployment, 
  DeploymentStatus, 
  EnvironmentType, 
  ResourceTemplate,
  DeploymentEvent,
  CreateDeploymentRequest 
} from '../lib/api';

// Default templates if server doesn't return any
const DEFAULT_TEMPLATES: ResourceTemplate[] = [
  {
    id: 'small-dev',
    name: 'Small Dev',
    machineType: 'e2-micro',
    diskSizeGb: 20,
    preemptible: false,
    estimatedMonthlyCost: 9,
    recommended: ['dev'],
  },
  {
    id: 'standard',
    name: 'Standard',
    machineType: 'e2-small',
    diskSizeGb: 50,
    preemptible: false,
    estimatedMonthlyCost: 30,
    recommended: ['test', 'staging'],
  },
  {
    id: 'production',
    name: 'Production Scale',
    machineType: 'e2-standard-2',
    diskSizeGb: 100,
    preemptible: false,
    estimatedMonthlyCost: 60,
    recommended: ['production'],
  },
  {
    id: 'demo',
    name: 'Demo',
    machineType: 'e2-micro',
    diskSizeGb: 20,
    preemptible: true,
    estimatedMonthlyCost: 3,
    recommended: ['demo'],
  },
];

export function Deployments() {
  const { activeProduct, isLoadingProduct } = useApp();
  const { toast } = useToast();
  
  // State
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [templates, setTemplates] = useState<ResourceTemplate[]>(DEFAULT_TEMPLATES);
  const [isLoading, setIsLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);
  const [deploymentEvents, setDeploymentEvents] = useState<DeploymentEvent[]>([]);
  const [operatingDeployments, setOperatingDeployments] = useState<Record<string, string>>({});
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState<EnvironmentType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<DeploymentStatus | 'all'>('all');

  // Load deployments
  const loadDeployments = useCallback(async () => {
    if (!activeProduct) return;
    
    setIsLoading(true);
    try {
      const result = await listDeployments({ productId: activeProduct.id });
      
      if (result.success && result.deployments) {
        setDeployments(result.deployments);
      } else {
        console.error('Failed to load deployments:', result.error);
        // Don't show error toast on empty results - it's expected for new products
      }
    } catch (error) {
      console.error('Error loading deployments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activeProduct]);

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      const result = await getResourceTemplates();
      
      if (result.success && result.templates && result.templates.length > 0) {
        setTemplates(result.templates);
      }
      // Keep default templates if API doesn't return any
    } catch (error) {
      console.error('Error loading templates:', error);
      // Keep default templates
    }
  }, []);

  // Load deployment events for selected deployment
  const loadDeploymentEvents = useCallback(async (deploymentId: string) => {
    try {
      const result = await getDeploymentEvents(deploymentId, 10);
      
      if (result.success && result.events) {
        setDeploymentEvents(result.events);
      }
    } catch (error) {
      console.error('Error loading deployment events:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (isLoadingProduct) {
      console.log('[Deployments] Waiting for active product to load');
      return;
    }
    
    if (!activeProduct) {
      console.log('[Deployments] No active product - skipping data load');
      setDeployments([]);
      return;
    }
    
    void loadDeployments();
    void loadTemplates();
  }, [loadDeployments, loadTemplates, isLoadingProduct, activeProduct]);

  // Load events when deployment is selected
  useEffect(() => {
    if (selectedDeployment) {
      void loadDeploymentEvents(selectedDeployment.id);
    } else {
      setDeploymentEvents([]);
    }
  }, [selectedDeployment, loadDeploymentEvents]);

  // Helper to mark deployment as operating
  const setDeploymentOperating = (deploymentId: string, operation: string | null) => {
    setOperatingDeployments(prev => {
      if (operation === null) {
        const newState = { ...prev };
        delete newState[deploymentId];
        return newState;
      }
      return { ...prev, [deploymentId]: operation };
    });
  };

  // Create deployment
  const handleCreateDeployment = async (request: CreateDeploymentRequest) => {
    setIsCreating(true);
    
    try {
      const result = await createDeployment(request);
      
      if (result.success && result.deployment) {
        toast({
          title: 'Deployment created',
          description: `${result.deployment.environmentName} is being provisioned.`,
        });
        setShowWizard(false);
        await loadDeployments();
      } else {
        toast({
          title: 'Failed to create deployment',
          description: result.error || 'An error occurred while creating the deployment.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to create deployment',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Deployment operations
  const handleStartDeployment = async (deployment: Deployment) => {
    setDeploymentOperating(deployment.id, 'start');
    
    try {
      const result = await startDeployment(deployment.id);
      
      if (result.success) {
        toast({
          title: 'Starting deployment',
          description: `${deployment.environmentName} is starting up.`,
        });
        await loadDeployments();
      } else {
        toast({
          title: 'Failed to start deployment',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to start deployment',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeploymentOperating(deployment.id, null);
    }
  };

  const handleStopDeployment = async (deployment: Deployment) => {
    setDeploymentOperating(deployment.id, 'stop');
    
    try {
      const result = await stopDeployment(deployment.id);
      
      if (result.success) {
        toast({
          title: 'Stopping deployment',
          description: `${deployment.environmentName} is stopping.`,
        });
        await loadDeployments();
      } else {
        toast({
          title: 'Failed to stop deployment',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to stop deployment',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeploymentOperating(deployment.id, null);
    }
  };

  const handleRestartDeployment = async (deployment: Deployment) => {
    setDeploymentOperating(deployment.id, 'restart');
    
    try {
      const result = await restartDeployment(deployment.id);
      
      if (result.success) {
        toast({
          title: 'Restarting deployment',
          description: `${deployment.environmentName} is restarting.`,
        });
        await loadDeployments();
      } else {
        toast({
          title: 'Failed to restart deployment',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to restart deployment',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeploymentOperating(deployment.id, null);
    }
  };

  const handleUpdateDeployment = async (deployment: Deployment) => {
    setDeploymentOperating(deployment.id, 'update');
    
    try {
      const result = await deployFromGit(deployment.id);
      
      if (result.success) {
        toast({
          title: 'Updating deployment',
          description: `${deployment.environmentName} is updating from git.`,
        });
        await loadDeployments();
      } else {
        toast({
          title: 'Failed to update deployment',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to update deployment',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeploymentOperating(deployment.id, null);
    }
  };

  const handleDeleteDeployment = async (deployment: Deployment) => {
    setDeploymentOperating(deployment.id, 'delete');
    
    try {
      const result = await deleteDeployment(deployment.id);
      
      if (result.success) {
        toast({
          title: 'Deployment deleted',
          description: `${deployment.environmentName} has been deleted.`,
        });
        setSelectedDeployment(null);
        await loadDeployments();
      } else {
        toast({
          title: 'Failed to delete deployment',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to delete deployment',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeploymentOperating(deployment.id, null);
    }
  };

  const handleRunMigrations = async () => {
    if (!selectedDeployment) return;
    
    setDeploymentOperating(selectedDeployment.id, 'migrate');
    
    try {
      const result = await runDatabaseMigrations(selectedDeployment.id);
      
      if (result.success) {
        toast({
          title: 'Migrations running',
          description: 'Database migrations are being applied.',
        });
      } else {
        toast({
          title: 'Failed to run migrations',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to run migrations',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeploymentOperating(selectedDeployment.id, null);
    }
  };

  const handleCreateBackup = async () => {
    if (!selectedDeployment) return;
    
    setDeploymentOperating(selectedDeployment.id, 'backup');
    
    try {
      const result = await createDatabaseBackup(selectedDeployment.id);
      
      if (result.success) {
        toast({
          title: 'Backup started',
          description: 'Database backup is being created.',
        });
      } else {
        toast({
          title: 'Failed to create backup',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to create backup',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeploymentOperating(selectedDeployment.id, null);
    }
  };

  const handleViewLogs = (deployment: Deployment) => {
    // For now, just show a toast - log streaming will be implemented
    toast({
      title: 'View Logs',
      description: `Log streaming for ${deployment.environmentName} coming soon.`,
    });
  };

  // Filter deployments
  const filteredDeployments = useMemo(() => {
    return deployments.filter(deployment => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = 
          deployment.environmentName.toLowerCase().includes(search) ||
          deployment.domain?.toLowerCase().includes(search) ||
          deployment.gitBranch?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Environment filter
      if (environmentFilter !== 'all' && deployment.environmentType !== environmentFilter) {
        return false;
      }
      
      // Status filter
      if (statusFilter !== 'all' && deployment.status !== statusFilter) {
        return false;
      }
      
      return true;
    });
  }, [deployments, searchTerm, environmentFilter, statusFilter]);

  const hasActiveFilters = searchTerm !== '' || environmentFilter !== 'all' || statusFilter !== 'all';

  const handleClearFilters = () => {
    setSearchTerm('');
    setEnvironmentFilter('all');
    setStatusFilter('all');
  };

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Cloud style={{ marginRight: '12px', color: '#0066cc' }} />
            <h2 style={{ margin: 0 }}>Deployments</h2>
          </div>
          <button
            className="btn"
            onClick={() => setShowWizard(true)}
            disabled={!activeProduct}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: activeProduct ? '#0066cc' : '#e5e7eb',
              color: activeProduct ? 'white' : '#9ca3af',
              cursor: activeProduct ? 'pointer' : 'not-allowed',
            }}
          >
            <Plus size={16} />
            New Deployment
          </button>
        </div>

        {/* Product context */}
        {activeProduct && (
          <div style={{ marginBottom: '16px', fontSize: '12px', color: '#2563eb' }}>
            Managing deployments for <span style={{ fontWeight: 500 }}>{activeProduct.title}</span>
          </div>
        )}

        {/* No product selected */}
        {!activeProduct && !isLoadingProduct && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
            <Server size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>No product selected</p>
            <p style={{ fontSize: '14px' }}>
              Select a product from the Products page to manage its deployments.
            </p>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
            <Loader size={48} style={{ marginBottom: '16px', opacity: 0.5, animation: 'spin 1s linear infinite' }} />
            <p>Loading deployments...</p>
          </div>
        )}

        {/* Deployments list */}
        {activeProduct && !isLoading && (
          <>
            <DeploymentFilters
              searchTerm={searchTerm}
              environmentFilter={environmentFilter}
              statusFilter={statusFilter}
              onSearchChange={setSearchTerm}
              onEnvironmentFilterChange={setEnvironmentFilter}
              onStatusFilterChange={setStatusFilter}
              onClearFilters={handleClearFilters}
              hasActiveFilters={hasActiveFilters}
            />

            <DeploymentListHeader
              filteredCount={filteredDeployments.length}
              totalCount={deployments.length}
              isLoading={isLoading}
              onRefresh={loadDeployments}
            />

            {deployments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
                <Cloud size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>No deployments yet</p>
                <p style={{ fontSize: '14px', marginBottom: '20px' }}>
                  Create your first deployment to host your product.
                </p>
                <button
                  className="btn"
                  onClick={() => setShowWizard(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: '#0066cc',
                    color: 'white',
                  }}
                >
                  <Plus size={16} />
                  New Deployment
                </button>
              </div>
            ) : filteredDeployments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
                <Cloud size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>No matching deployments</p>
                <p style={{ fontSize: '14px' }}>
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {filteredDeployments.map((deployment) => (
                  <DeploymentCard
                    key={deployment.id}
                    deployment={deployment}
                    isOperating={!!operatingDeployments[deployment.id]}
                    operationInProgress={operatingDeployments[deployment.id]}
                    onStart={() => handleStartDeployment(deployment)}
                    onStop={() => handleStopDeployment(deployment)}
                    onRestart={() => handleRestartDeployment(deployment)}
                    onUpdate={() => handleUpdateDeployment(deployment)}
                    onViewLogs={() => handleViewLogs(deployment)}
                    onViewDetails={() => setSelectedDeployment(deployment)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Deployment Wizard */}
      {showWizard && activeProduct && (
        <DeploymentWizard
          productId={activeProduct.id}
          productName={activeProduct.title}
          templates={templates}
          isCreating={isCreating}
          onClose={() => setShowWizard(false)}
          onCreate={handleCreateDeployment}
        />
      )}

      {/* Deployment Details Panel */}
      {selectedDeployment && (
        <DeploymentDetails
          deployment={selectedDeployment}
          events={deploymentEvents}
          isOperating={!!operatingDeployments[selectedDeployment.id]}
          operationInProgress={operatingDeployments[selectedDeployment.id]}
          onClose={() => setSelectedDeployment(null)}
          onStart={() => handleStartDeployment(selectedDeployment)}
          onStop={() => handleStopDeployment(selectedDeployment)}
          onRestart={() => handleRestartDeployment(selectedDeployment)}
          onUpdate={() => handleUpdateDeployment(selectedDeployment)}
          onDelete={() => handleDeleteDeployment(selectedDeployment)}
          onViewLogs={() => handleViewLogs(selectedDeployment)}
          onRunMigrations={handleRunMigrations}
          onCreateBackup={handleCreateBackup}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

